/** Junta o que o motor de visualização (Fragments) devolve de um
 *  elemento com o que a leitura em bruto do IFC (web-ifc) tem.
 *
 *  Regra geral: o motor ganha, e o bruto só preenche o que falta (as
 *  entidades sem geometria, IfcSystem, IfcZone..., chegam quase vazias
 *  do motor).
 *
 *  Excepção: a Elevation do piso e a RefElevation do site. O importador
 *  do Fragments substitui-as pela cota absoluta, em metros
 *  (replaceStoreyElevation / replaceSiteElevation), e o painel mostrava
 *  "Elevation 3" num ficheiro em mm que declara 3000. Aqui vale o que o
 *  ficheiro declara; o valor do motor fica à parte, para o painel dizer
 *  numa linha discreta que o motor usa a cota absoluta. As opções do
 *  importador não se mudam: mudariam o conteúdo da cache .frag. */

/** Atributos que o motor reescreve, por classe (em maiúsculas). */
const REESCRITOS_PELO_MOTOR: Record<string, string> = {
  IFCBUILDINGSTOREY: 'Elevation',
  IFCSITE: 'RefElevation',
}

export interface CotaDoMotor {
  atributo: string
  /** O valor que o motor guarda (cota absoluta, em metros). */
  valor: unknown
  /** true quando não há leitura do ficheiro (um .frag): o painel mostra
   *  o valor do motor e diz que é esse. */
  semFicheiro?: boolean
}

function numero(v: unknown): string {
  const x = v && typeof v === 'object' && 'value' in (v as object) ? (v as { value: unknown }).value : v
  return typeof x === 'number' ? String(Number(x.toFixed(6))) : String(x ?? '')
}

/** A linha discreta que o painel mostra por baixo dos atributos. */
export function textoDaCotaDoMotor(c: CotaDoMotor): string {
  if (c.semFicheiro) {
    return `${c.atributo}: valor do motor de visualização (cota absoluta, em metros). ` +
      'Sem o ficheiro IFC não se lê o valor declarado.'
  }
  return `${c.atributo}: o valor acima é o que o ficheiro declara. O motor de visualização ` +
    `usa a cota absoluta, em metros (${numero(c.valor)} m).`
}

export interface DadosJuntos {
  dados: Record<string, unknown>
  /** Presente quando o atributo do ficheiro substituiu o do motor. */
  cotaDoMotor: CotaDoMotor | null
}

function vazio(v: unknown): boolean {
  return v === null || v === undefined
}

export function juntarComLeituraEmBruto(
  doMotor: Record<string, unknown> | null,
  emBruto: Record<string, unknown> | null,
  categoria: string,
): DadosJuntos {
  if (!emBruto) return { dados: doMotor ?? {}, cotaDoMotor: null }
  const out: Record<string, unknown> = doMotor ? { ...doMotor } : {}
  for (const [k, v] of Object.entries(emBruto)) {
    if (vazio(v)) continue
    if (k in out && !vazio(out[k])) continue
    out[k] = v
  }
  let cotaDoMotor: CotaDoMotor | null = null
  const atributo = REESCRITOS_PELO_MOTOR[categoria.toUpperCase()]
  if (atributo && !vazio(emBruto[atributo]) && doMotor && !vazio(doMotor[atributo])) {
    cotaDoMotor = { atributo, valor: doMotor[atributo] }
    out[atributo] = emBruto[atributo]
  }
  return { dados: out, cotaDoMotor }
}

/** A área de uma fronteira de espaço (IfcRelSpaceBoundary), calculada da
 *  geometria da ligação na unidade de comprimento do ficheiro ao
 *  quadrado. "12.34 mm² (calculado)"; sem unidade de comprimento
 *  declarada, não se assume o metro. */
export function textoDaAreaCalculada(area: number, unidadeDeComprimento: string | null): string {
  const n = area.toFixed(2)
  return unidadeDeComprimento
    ? `${n} ${unidadeDeComprimento}² (calculado)`
    : `${n} (unidade não declarada, calculado)`
}
